import {toBaseScalarPricingFormValues} from "@/features/pricing";
import {CallDurationOptions} from "../../../../components/offering-form/session-bundle/session/gen-info/session-duration/call-duration-form-field";
import {GetSessionApiResponse} from "../../../../react-query-hooks/session/types/edit-session";
import {EditSessionBasicFormStepFormValues} from "../schema-validation";

/**
 * The saved session in the edit page's form encoding.
 *
 * A tab that edits one part of the session, such as the checkout questions, still sends
 * the whole update, so everything else must go back exactly as saved. The form tab uses
 * this as its starting values and the checkout tab submits it with its questions on top,
 * so the two cannot drift.
 *
 * Cover images are left out: each tab loads them separately.
 *
 * @param preferredScheduleId - the form tab's fallback for a legacy session with no
 *   schedule of its own. The session's own schedule always wins.
 */
export function toSavedEditSessionFormValues(
  sessionData: GetSessionApiResponse,
  isPwywEnabled: boolean,
  preferredScheduleId?: string
): Omit<EditSessionBasicFormStepFormValues, "cover_images"> {
  const savedRedirectType =
    sessionData.redirectType ?? (sessionData.url_redirect ? "url" : "none");
  const savedPricing = toBaseScalarPricingFormValues(sessionData, isPwywEnabled);
  const isListedDuration =
    CallDurationOptions.find(
      (ele) => ele.value === sessionData.callDuration.toString()
    ) !== undefined;

  return {
    callDuration: isListedDuration
      ? sessionData.callDuration.toString()
      : !sessionData.callDuration
      ? sessionData.callDuration.toString()
      : "-1",
    callLocation: (sessionData as any).hasGoogleMeet
      ? "google_meet"
      : sessionData.videoPlatform === "zoom"
      ? "zoom"
      : sessionData.videoLocation
      ? "video"
      : "phone",
    callPrice: savedPricing.callPrice,
    callPriceCurrency: savedPricing.callPriceCurrency,
    callPriceType: savedPricing.callPriceType,
    coverImage: sessionData.cover_picture,
    // Saved rows always carry an amount; the form's row type just does not say so.
    currencies: savedPricing.currencies.map((row) => ({
      ...row,
      callPrice: row.callPrice ?? 0,
    })),
    enable_currency_conversion: true,
    selected_currencies: sessionData.currencies
      .filter((x) => x.id)
      .filter((c) => c.is_auto_converted)
      .map((c) => c.currency.toUpperCase()),
    customCallDuration: isListedDuration ? undefined : sessionData.callDuration,
    description: sessionData.description,
    hideFromProfile: sessionData.hideServiceFromMenu,
    isPaused: sessionData.statusName === "Paused",
    isUrlRedirect: !!sessionData.url_redirect,
    redirectAfterPurchase: savedRedirectType === "url" ? "custom-url" : savedRedirectType,
    telegramGroupId: sessionData.telegramGroupChatId ?? undefined,
    name: sessionData.name,
    requestCustomerPhoneNumber: sessionData.request_phone_number,
    slug: sessionData.slug,
    url_redirect: sessionData.url_redirect,
    // The session's own schedule wins verbatim — never reconciled against the fetched
    // list, since that list is paginated and a match failure would silently reassign a
    // live offering. The fallback only covers legacy sessions with no schedule at all,
    // which the adapter surfaces as an empty id.
    availabilityScheduleId: sessionData.schedule.id || preferredScheduleId || "",
  };
}
